import { useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, Card, Col, Collapse, Divider, Input,
  Row, Space, Spin, Table, Tag, Typography, message,
} from 'antd';
import {
  CheckCircleFilled, CloseCircleFilled, CopyOutlined,
  EditOutlined, SaveOutlined, CloseOutlined, WarningOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { Panel } = Collapse;

function toLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function FieldGrid({ data, editing, onChange, lowConfFields = [] }) {
  const entries = Object.entries(data).filter(([, v]) => !Array.isArray(v));
  return (
    <Row gutter={[8, 8]}>
      {entries.map(([key, value]) => {
        const isLow = lowConfFields.some(f => f.toLowerCase().startsWith(key.toLowerCase()));
        return (
          <Col xs={24} sm={12} key={key}>
            <div style={{
              padding: '8px 10px',
              background: isLow ? '#fffbeb' : '#fafafa',
              border: `1px solid ${isLow ? '#f4d03f' : '#e8e8e8'}`,
              borderRadius: 6,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>
                {toLabel(key)}
                {isLow && <Tag color="warning" style={{ marginLeft: 6, fontSize: 10 }}>Low confidence</Tag>}
              </div>
              {editing ? (
                <Input
                  size="small"
                  value={value ?? ''}
                  onChange={e => onChange(key, e.target.value || null)}
                  placeholder="—"
                  style={{ fontSize: 13 }}
                />
              ) : (
                <div style={{ fontSize: 13, fontWeight: 500, color: value ? '#141414' : '#bfbfbf', fontStyle: value ? 'normal' : 'italic' }}>
                  {value ?? '—'}
                </div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
}

function SamplesSection({ samples, editing, onChange }) {
  if (!samples || samples.length === 0) return null;
  const columns = Object.keys(samples[0]);

  if (!editing) {
    const tableColumns = [
      { title: '#', dataIndex: '_idx', width: 40, render: (_, __, i) => i + 1 },
      ...columns.map(col => ({
        title: toLabel(col),
        dataIndex: col,
        render: val => val ?? <Text type="secondary" italic>—</Text>,
      })),
    ];
    return (
      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <Table
          size="small"
          dataSource={samples.map((r, i) => ({ ...r, _idx: i, key: i }))}
          columns={tableColumns}
          pagination={false}
          bordered
        />
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#141414' }}>
            <th style={{ color: '#fff', padding: '6px 8px', textAlign: 'left' }}>#</th>
            {columns.map(col => (
              <th key={col} style={{ color: '#fff', padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                {toLabel(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {samples.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ padding: '4px 8px', color: '#8c8c8c', fontWeight: 600 }}>{i + 1}</td>
              {columns.map(col => (
                <td key={col} style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>
                  <Input
                    size="small"
                    value={row[col] ?? ''}
                    onChange={e => {
                      const updated = [...samples];
                      updated[i] = { ...updated[i], [col]: e.target.value || null };
                      onChange(updated);
                    }}
                    style={{ minWidth: 90 }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FileResultCard({ result: initialResult, index, apiBase }) {
  const [result, setResult]           = useState(initialResult);
  const [editing, setEditing]         = useState(false);
  const [editData, setEditData]       = useState(null);
  const [editSamples, setEditSamples] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);

  const {
    filename, formLabel, success,
    referenceNumber, errorMessage, reviewRequired,
    lowConfidenceFields = [],
    extractedData = {}, samples = [],
  } = result;

  function startEdit() {
    setEditData({ ...extractedData });
    setEditSamples(samples.map(r => ({ ...r })));
    setEditing(true);
    setSaved(false);
  }

  function cancelEdit() {
    setEditing(false);
    setEditData(null);
    setEditSamples(null);
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await axios.put(`${apiBase}/sessions/${referenceNumber}`, {
        extractedData: editData,
        samples:       editSamples,
      });
      setResult(prev => ({ ...prev, extractedData: editData, samples: editSamples }));
      setEditing(false);
      setEditData(null);
      setEditSamples(null);
      setSaved(true);
      message.success(`${referenceNumber} updated successfully`);
    } catch (err) {
      message.error('Update failed: ' + (err.response?.data || err.message));
    } finally {
      setSaving(false);
    }
  }

  const displayData    = editing ? editData    : extractedData;
  const displaySamples = editing ? editSamples : samples;
  const hasData        = displayData && Object.keys(displayData).length > 0;
  const hasSamples     = displaySamples && displaySamples.length > 0;

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, borderLeft: `3px solid ${success ? '#52c41a' : '#ff4d4f'}` }}
      bodyStyle={{ padding: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: success && hasData ? 12 : 0 }}>
        {success
          ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
          : <CloseCircleFilled  style={{ color: '#ff4d4f', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ display: 'block', wordBreak: 'break-all', fontSize: 13 }}>
            {filename || `File ${index + 1}`}
          </Text>
          <Space wrap size={4} style={{ marginTop: 4 }}>
            {formLabel && <Tag style={{ fontSize: 11 }}>{formLabel}</Tag>}
            {success && (
              <Text copyable={{ icon: <CopyOutlined />, tooltips: ['Copy ref', 'Copied'] }} style={{ fontSize: 12 }}>
                <strong>{referenceNumber}</strong>
              </Text>
            )}
            {saved && <Tag color="success">✓ Saved</Tag>}
            {reviewRequired && <Tag color="warning" icon={<WarningOutlined />}>Review required</Tag>}
            {lowConfidenceFields.length > 0 && (
              <Tag color="orange">{lowConfidenceFields.length} low confidence</Tag>
            )}
          </Space>
          {!success && (
            <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {errorMessage || 'Extraction failed'}
            </Text>
          )}
        </div>

        {success && hasData && (
          <Space size={6} style={{ flexShrink: 0 }}>
            {editing ? (
              <>
                <Button size="small" type="primary" icon={saving ? <Spin size="small" /> : <SaveOutlined />} onClick={saveEdit} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit} disabled={saving}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button size="small" icon={<EditOutlined />} onClick={startEdit}>Edit</Button>
            )}
          </Space>
        )}
      </div>

      {success && hasData && (
        <Collapse ghost defaultActiveKey={reviewRequired ? ['fields'] : []} style={{ marginTop: 4 }}>
          <Panel
            header={
              <Text style={{ fontSize: 12, fontWeight: 600, color: '#595959' }}>
                Extracted Fields {editing && <Tag color="processing" style={{ marginLeft: 6 }}>Editing</Tag>}
              </Text>
            }
            key="fields"
          >
            <FieldGrid
              data={displayData}
              editing={editing}
              onChange={(key, val) => setEditData(prev => ({ ...prev, [key]: val }))}
              lowConfFields={lowConfidenceFields}
            />
            {hasSamples && (
              <>
                <Divider orientation="left" plain style={{ fontSize: 12, color: '#8c8c8c', margin: '12px 0 8px' }}>
                  Samples ({displaySamples.length} row{displaySamples.length !== 1 ? 's' : ''})
                </Divider>
                <SamplesSection samples={displaySamples} editing={editing} onChange={setEditSamples} />
              </>
            )}
          </Panel>
        </Collapse>
      )}
    </Card>
  );
}

export default function BatchResultDisplay({ result, apiBase }) {
  if (!result) return null;

  const { testCode, totalFiles, successCount, failureCount, results = [] } = result;

  const allSuccess = failureCount === 0;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      <Alert
        type={allSuccess ? 'success' : 'warning'}
        showIcon
        message={
          allSuccess
            ? `${successCount} of ${totalFiles} form${totalFiles !== 1 ? 's' : ''} processed successfully`
            : `${successCount} succeeded — ${failureCount} failed`
        }
        description={testCode ? `Test Code: ${testCode}` : undefined}
      />

      <Text type="secondary" style={{ fontSize: 12 }}>
        Tap a result to expand fields · Use Edit to correct any values · Save updates the database
      </Text>

      {results.map((r, i) => (
        <FileResultCard key={r.referenceNumber ?? i} result={r} index={i} apiBase={apiBase} />
      ))}
    </Space>
  );
}